import { injectable } from 'inversify';
import { Permission } from '../../constants/Permission';
import { PermissionsProvider } from './PermissionsProvider';

// NOTE: Ordering and numbers are significant - they match the backend.
enum AccessLevel {
  MANAGE = 0,
  PUBLISH = 1,
  DRAFT = 2,
  VIEW = 3,
  NO_ACCESS = 4
}

// NOTE: Ordering and numbers are significant - they match the backend.
enum Capability {
  MESSAGES = 0,
  VARIABLES = 1,
  INTERFACES = 2,
  AUDIENCES = 3,
  ANALYTICS = 4,
  APP = 5,
  TEAM = 6,
  DATA_CONTROL = 7
}

@injectable()
export class StaticPermissionsProvider implements PermissionsProvider {
  private campaignAccessLevel: number;

  constructor(private capabilities: Array<number>, private hasLimitToAudiences: boolean = false) {
    // For CC, use the "higher" permission of messages & variables
    // (in this case, lower enum value = higher permission level)
    this.campaignAccessLevel = Math.min(
      this.capabilities[Capability.MESSAGES],
      this.capabilities[Capability.VARIABLES]
    );
  }

  hasPermission(permission: Permission): boolean {
    switch (permission) {
      case Permission.VIEW_AUDIENCE:
        return this.capabilities[Capability.AUDIENCES] <= AccessLevel.VIEW;
      case Permission.DRAFT_APP_ASSETS:
      case Permission.DRAFT_CAMPAIGN:
        return this.campaignAccessLevel <= AccessLevel.DRAFT;
      case Permission.PUBLISH_AUDIENCE:
        return this.capabilities[Capability.AUDIENCES] <= AccessLevel.PUBLISH;
      case Permission.PUBLISH_CAMPAIGN:
        return this.campaignAccessLevel <= AccessLevel.PUBLISH;
      case Permission.MANAGE_AUDIENCE:
        return !this.hasLimitToAudiences && this.capabilities[Capability.AUDIENCES] === AccessLevel.MANAGE;
      case Permission.MANAGE_CAMPAIGN:
        return !this.hasLimitToAudiences && this.campaignAccessLevel === AccessLevel.MANAGE;
      case Permission.MANAGE_DATA_CONTROL:
        return this.capabilities[Capability.DATA_CONTROL] === AccessLevel.MANAGE;
      default:
        return false;
    }
  }
}
